// frontend/src/pages/referee/Dashboard.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import Footer from '../../components/Footer';
import api from '../../services/api';

const Dashboard = ({ user, setUser }) => {
  const [fiscalYear, setFiscalYear] = useState(null);
  const [systemSettings, setSystemSettings] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Fetch active fiscal year
        const fiscalYearRes = await api.get('/fiscal-years/active');
        const activeFiscalYear = fiscalYearRes.data;
        setFiscalYear(activeFiscalYear);

        if (activeFiscalYear) {
          // Fetch system settings
          const settingsRes = await api.get(`/system-settings/${activeFiscalYear.id}`);
          setSystemSettings(settingsRes.data);

          // Fetch referee assignments
          const assignmentsRes = await api.get(`/referee-assignments/referee/${user.id}/${activeFiscalYear.id}`);
          setAssignments(assignmentsRes.data);

          // Fetch completed evaluations
          const evaluationsRes = await api.get(`/oit/evaluations/${user.id}/${activeFiscalYear.id}`);
          setEvaluations(evaluationsRes.data);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [user.id]);

  // Group evaluations by school
  const evaluationsBySchool = evaluations.reduce((acc, evaluation) => {
    if (!acc[evaluation.school_name]) {
      acc[evaluation.school_name] = [];
    }
    acc[evaluation.school_name].push(evaluation);
    return acc;
  }, {});

  // Check if current date is within evaluation period
  const isEvaluationPeriodActive = () => {
    if (!systemSettings) return false;
    
    const startDate = new Date(systemSettings.evaluation_start_date);
    const endDate = new Date(systemSettings.evaluation_end_date);
    const now = new Date();
    
    return now >= startDate && now <= endDate;
  };

  return (
    <div className="wrapper">
      <Header user={user} title="แดชบอร์ด" />
      <Sidebar user={user} />

      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0">แดชบอร์ด</h1>
              </div>
              <div className="col-sm-6">
                <ol className="breadcrumb float-sm-right">
                  <li className="breadcrumb-item"><Link to="/referee/dashboard">หน้าหลัก</Link></li>
                  <li className="breadcrumb-item active">แดชบอร์ด</li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        <section className="content">
          <div className="container-fluid">
            {loading ? (
              <div className="text-center my-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">กำลังโหลด...</span>
                </div>
              </div>
            ) : (
              <>
                <div className="row">
                  <div className="col-lg-3 col-6">
                    <div className="small-box bg-info">
                      <div className="inner">
                        <h3>{assignments.length}</h3>
                        <p>รายการที่ได้รับมอบหมาย</p>
                      </div>
                      <div className="icon">
                        <i className="fas fa-tasks"></i>
                      </div>
                      <Link to="/referee/evaluate-oit" className="small-box-footer">
                        ข้อมูลเพิ่มเติม <i className="fas fa-arrow-circle-right"></i>
                      </Link>
                    </div>
                  </div>

                  <div className="col-lg-3 col-6">
                    <div className="small-box bg-success">
                      <div className="inner">
                        <h3>{evaluations.length}</h3>
                        <p>รายการที่ประเมินแล้ว</p>
                      </div>
                      <div className="icon">
                        <i className="fas fa-check-square"></i>
                      </div>
                      <Link to="/referee/evaluate-oit" className="small-box-footer">
                        ข้อมูลเพิ่มเติม <i className="fas fa-arrow-circle-right"></i>
                      </Link>
                    </div>
                  </div>

                  <div className="col-lg-3 col-6">
                    <div className="small-box bg-warning">
                      <div className="inner">
                        <h3>{Object.keys(evaluationsBySchool).length}</h3>
                        <p>โรงเรียนที่ประเมินแล้ว</p>
                      </div>
                      <div className="icon">
                        <i className="fas fa-school"></i>
                      </div>
                      <a href="#schools" className="small-box-footer">
                        ข้อมูลเพิ่มเติม <i className="fas fa-arrow-circle-right"></i>
                      </a>
                    </div>
                  </div>

                  <div className="col-lg-3 col-6">
                    <div className="small-box bg-danger">
                      <div className="inner">
                        <h3>{fiscalYear ? fiscalYear.year : '-'}</h3>
                        <p>ปีงบประมาณปัจจุบัน</p>
                      </div>
                      <div className="icon">
                        <i className="fas fa-calendar-alt"></i>
                      </div>
                      <a href="#" className="small-box-footer">
                        &nbsp;
                      </a>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-12">
                    <div className="card">
                      <div className="card-header">
                        <h3 className="card-title">สถานะการประเมิน</h3>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-md-6">
                            <table className="table table-bordered">
                              <tbody>
                                <tr>
                                  <th style={{ width: '40%' }}>ช่วงเวลาการประเมิน</th>
                                  <td>
                                    {systemSettings ? (
                                      <>
                                        {new Date(systemSettings.evaluation_start_date).toLocaleDateString('th-TH')} ถึง {' '}
                                        {new Date(systemSettings.evaluation_end_date).toLocaleDateString('th-TH')}
                                      </>
                                    ) : '-'}
                                  </td>
                                </tr>
                                <tr>
                                  <th>สถานะการประเมิน</th>
                                  <td>
                                    <span className={`badge ${isEvaluationPeriodActive() ? 'bg-success' : 'bg-danger'}`}>
                                      {isEvaluationPeriodActive() ? 'เปิดให้ประเมิน' : 'ปิดการประเมิน'}
                                    </span>
                                  </td>
                                </tr>
                                <tr>
                                  <th>การดำเนินการ</th>
                                  <td>
                                    <Link 
                                      to="/referee/evaluate-oit" 
                                      className={`btn btn-${isEvaluationPeriodActive() ? 'primary' : 'secondary'} btn-sm`}
                                      disabled={!isEvaluationPeriodActive()}
                                    >
                                      <i className="fas fa-check-square"></i> ประเมิน OIT
                                    </Link>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                          <div className="col-md-6">
                            <div className="progress-group">
                              <span className="progress-text">ความก้าวหน้าในการประเมิน</span>
                              <span className="float-right">
                                <b>{evaluations.length}</b>/{assignments.length} รายการ
                              </span>
                              <div className="progress">
                                <div 
                                  className="progress-bar bg-primary" 
                                  style={{ width: `${assignments.length > 0 ? (evaluations.length / assignments.length) * 100 : 0}%` }}>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-12">
                    <div className="card">
                      <div className="card-header">
                        <h3 className="card-title">รายการที่ได้รับมอบหมาย</h3>
                      </div>
                      <div className="card-body p-0">
                        <table className="table table-striped">
                          <thead>
                            <tr>
                              <th style={{ width: '5%' }}>#</th>
                              <th>ตัวชี้วัด</th>
                              <th style={{ width: '20%' }}>สถานะ</th>
                            </tr>
                          </thead>
                          <tbody>
                            {assignments.length > 0 ? (
                              assignments.map((assignment, index) => {
                                const isEvaluated = evaluations.some(e => 
                                  e.question_text === assignment.question_text
                                );
                                
                                return (
                                  <tr key={assignment.id}>
                                    <td>{index + 1}</td>
                                    <td>{assignment.question_text}</td>
                                    <td>
                                      {isEvaluated ? (
                                        <span className="badge bg-success">ประเมินแล้ว</span>
                                      ) : (
                                        <span className="badge bg-warning">รอการประเมิน</span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr>
                                <td colSpan="3" className="text-center">ไม่พบรายการที่ได้รับมอบหมาย</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row" id="schools">
                  <div className="col-md-12">
                    <div className="card">
                      <div className="card-header">
                        <h3 className="card-title">โรงเรียนที่ประเมินแล้ว</h3>
                      </div>
                      <div className="card-body">
                        {Object.keys(evaluationsBySchool).length > 0 ? (
                          <div className="row">
                            {Object.keys(evaluationsBySchool).map(schoolName => (
                              <div className="col-md-4" key={schoolName}>
                                <div className="info-box">
                                  <span className="info-box-icon bg-info">
                                    <i className="fas fa-school"></i>
                                  </span>
                                  <div className="info-box-content">
                                    <span className="info-box-text">{schoolName}</span>
                                    <span className="info-box-number">
                                      {evaluationsBySchool[schoolName].length} รายการ
                                    </span>
                                    <div className="progress">
                                      <div className="progress-bar bg-info" style={{ width: '100%' }}></div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-center">ยังไม่มีโรงเรียนที่ประเมินแล้ว</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default Dashboard;